// The prompt outbox (#3850, #3629 C): the optimistic bubble's send lifecycle, the verdicts and
// the pre-turn wait. A busy agent's queue is the daemon's (CYBORG-1560). It writes the view through
// the history facade; what it reads of the store is OutboxHost.
import { typedCommandOf, type AgentCommandInput } from "../agent-command-input.js";
import type { Attachment } from "../../../types.js";
import type { PageCard } from "../../../core/types.js";
import type {
  ConversationHistory,
  ConversationTarget,
  ConversationWrite,
} from "./conversation-history.js";
import type { AgentStream, StreamEntry, TurnStatus } from "./types.js";
import { seatByTs } from "./view-ops.js";
import {
  markAgentPromptOutboxQueued,
  updateAgentPromptOutboxText,
} from "../../../cache/agent-prompt-outbox.js";
import { sessionStreamKey } from "../../../session-progress.js";

type UserMessageEntry = Extract<StreamEntry, { kind: "user_message" }>;

function withoutStatus(e: UserMessageEntry): UserMessageEntry {
  const { status: _status, ...rest } = e;
  return rest;
}

// The daemon holds it: queued, and whatever doubt the bubble carried (unconfirmed, reconnecting) is
// answered, since the daemon has it.
function heldByIt(e: UserMessageEntry): UserMessageEntry {
  const doubt = e.status === "unknown" || (e.status === "pending" && !!e.statusReason);
  return doubt
    ? { ...e, queued: true, status: undefined, statusReason: undefined, statusCode: undefined }
    : { ...e, queued: true };
}

// The bubble a prompt's verdict settles: unconfirmed, held, or (a refusal) any optimistic send,
// drawn direct or held, so a refused one never sits there as sent.
export function verdictSettles(
  row: UserMessageEntry,
  promptId: string,
  verdict: "accepted" | "unavailable",
): boolean {
  if (row.clientMessageId !== promptId) return false;
  return (
    row.status === "unknown" ||
    row.queued === true ||
    (verdict === "unavailable" && row.seq === undefined)
  );
}

function withoutQueued(e: UserMessageEntry): UserMessageEntry {
  const { queued: _queued, ...rest } = e;
  return rest;
}
import { mentionQueueState } from "../../../state/mention-queue.svelte.js";
import { statusCard } from "../status-card.js";
import { reportUiFailure } from "../../../report-failure.js";
import { answeredSince, type NotRunAnchor } from "../not-run-clear.js";
import {
  promptPendingUnconfirmedMessage,
  PROMPT_PENDING_WATCHDOG_MS,
  type RetrySend,
} from "../state.svelte.js";

// What the outbox reads and writes of the store.
export interface OutboxHost {
  readonly streams: Map<string, AgentStream>;
  readonly serverEchoedPromptIds: ReadonlySet<string>;
  readonly softStopPrompts: Map<string, { agentId: string; note: string }>;
  softStopRefusals: Set<string>;
  capEntries(w: ConversationWrite, s: AgentStream): void;
  ensure(agentId: string): AgentStream;
  getEntries(agentId: string): readonly StreamEntry[];
  getTurnStatus(agentId: string): TurnStatus;
  // The composer's "Queue" question (its turn or the roster says running); absent: the stream's own.
  sendWouldQueue?(agentId: string): boolean;
  nextEntryId(): string;
  softCancelTurn(target: ConversationTarget, note: string): void;
  touch(agentId?: string): void;
  workspaceForAgent(agentId: string): string | null;
}

export class PromptOutbox {
  constructor(
    private readonly history: ConversationHistory,
    private readonly host: OutboxHost,
  ) {}

  // agent_prompt_queued: marked queued and kept last; a queued entry is drawn only in the bar.
  // Per agent, the prompts that were queued and then RAN (turn start or accepted): a stale queue
  // frame must not label them Queued again. A prompt refused (unavailable) is not here, so its
  // retry can be held and labelled again.
  private readonly ranFromQueue = new Map<string, Set<string>>();

  private markRan(agentId: string, promptId: string): void {
    const ids = this.ranFromQueue.get(agentId) ?? new Set<string>();
    ids.add(promptId);
    this.ranFromQueue.set(agentId, ids);
  }

  // Per agent, the prompts the DAEMON said it holds; only those leave the bar on another turn's start.
  private readonly heldByDaemon = new Map<string, Set<string>>();

  private forgetHeld(agentId: string, promptId: string): void {
    this.heldByDaemon.get(agentId)?.delete(promptId);
  }

  // fresh: the daemon's own queued signal for this prompt, which is newer than any run of it (a
  // joined turn it put back after a Stop or a failure). A queue LIST may be stale, so it is not.
  markQueued(target: ConversationTarget, promptId: string, fresh = false): void {
    const marked = this.history.within(target, "outbox", (w) => {
      const s = this.host.streams.get(w.agentId);
      if (!s || this.host.serverEchoedPromptIds.has(promptId)) return false;
      if (fresh) this.ranFromQueue.get(w.agentId)?.delete(promptId);
      if (this.ranFromQueue.get(w.agentId)?.has(promptId)) return false;
      const local = s.entries.some(
        (e) => e.kind === "user_message" && e.clientMessageId === promptId && e.seq === undefined,
      );
      const confirmed = this.heldByDaemon.get(w.agentId) ?? new Set<string>();
      if (local && !confirmed.has(promptId)) {
        confirmed.add(promptId);
        this.heldByDaemon.set(w.agentId, confirmed);
        void markAgentPromptOutboxQueued(promptId);
      }
      const at = s.entries.findIndex(
        (e) => e.kind === "user_message" && e.clientMessageId === promptId && e.seq === undefined,
      );
      const entry = s.entries[at];
      // A failed bubble is refused, not held: only a retry (which sets it pending) can label it again.
      if (entry?.kind !== "user_message" || entry.queued || entry.status === "failed") return false;
      w.assign(s, [...s.entries.filter((_, i) => i !== at), heldByIt(entry)]);
      this.clearPromptPending(w.agentId);
      this.host.touch(w.agentId);
      return true;
    });
    if (marked) {
      this.updateSessionCopy(
        promptId,
        (e) => (e.kind === "user_message" ? heldByIt(e) : e),
        false,
        true,
      );
    }
  }

  // The author committed an edit of a queued prompt: its bubble takes the new words, like its row.
  // The queue frame carries only a truncated preview, so the full text comes from the commit.
  setQueuedText(target: ConversationTarget, promptId: string, text: string): void {
    // The durable row first: a reload restores the bubble from it, whatever is on screen now.
    void updateAgentPromptOutboxText(promptId, text);
    const reword = (e: StreamEntry): StreamEntry =>
      e.kind === "user_message" && e.queued ? { ...e, content: text } : e;
    const changed = this.history.within(target, "outbox", (w) => {
      const s = this.host.streams.get(w.agentId);
      const at = s?.entries.findIndex(
        (e) => e.kind === "user_message" && e.clientMessageId === promptId && e.queued === true,
      );
      if (!s || at === undefined || at < 0) return false;
      w.assign(
        s,
        s.entries.map((e, i) => (i === at ? reword(e) : e)),
      );
      this.host.touch(w.agentId);
      return true;
    });
    if (changed) this.updateSessionCopy(promptId, reword, false);
  }

  // A turn started: a queued prompt the daemon's queue no longer lists is the one running.
  settleQueuedOnTurnStart(target: ConversationTarget, listed: ReadonlySet<string>): void {
    const settled = this.history.within(target, "outbox", (w) => {
      const s = this.host.streams.get(w.agentId);
      if (!s) return [];
      const ids: string[] = [];
      const next = s.entries.map((e) => {
        if (e.kind !== "user_message" || !e.queued || !e.clientMessageId) return e;
        if (listed.has(e.clientMessageId)) return e;
        if (!this.heldByDaemon.get(w.agentId)?.has(e.clientMessageId)) return e;
        ids.push(e.clientMessageId);
        this.markRan(w.agentId, e.clientMessageId);
        this.forgetHeld(w.agentId, e.clientMessageId);
        return withoutQueued(e);
      });
      if (ids.length === 0) return ids;
      w.assign(s, next);
      this.host.touch(w.agentId);
      return ids;
    });
    for (const id of settled) {
      this.updateSessionCopy(id, (e) => (e.kind === "user_message" ? withoutQueued(e) : e));
    }
  }

  // A verdict: accepted settles the bubble in place; unavailable removes or fails it.
  settlePromptVerdict(
    target: ConversationTarget,
    promptId: string,
    verdict: "accepted" | "unavailable",
    reason?: string,
  ): void {
    let change: ((e: StreamEntry) => StreamEntry | null) | undefined;
    let takenBack = false;
    this.history.within(target, "verdict", (w) => {
      const agentId = w.agentId;
      // A soft stop is answered here too: accepted frees the composer, unavailable lets the human choose.
      const pendingSoftStop = this.host.softStopPrompts.get(promptId);
      if (pendingSoftStop) {
        this.host.softStopPrompts.delete(promptId);
        if (verdict === "accepted") this.host.softCancelTurn(w, pendingSoftStop.note);
        else this.host.softStopRefusals = new Set([...this.host.softStopRefusals, agentId]);
        this.host.touch(w.agentId);
        return;
      }
      // The only proof of life an ephemeral turn delivers (no agent_stream, no agent_status).
      if (verdict === "accepted") {
        // Accepted, queued or not: a stale queue frame must not label it afterwards.
        this.markRan(agentId, promptId);
        this.clearPromptPending(agentId);
        this.retractPromptPendingRow(w, promptId);
      }
      this.forgetHeld(agentId, promptId); // a verdict ends the hold, either way
      const s = this.host.streams.get(agentId);
      const entry = s?.entries.find(
        (row) => row.kind === "user_message" && verdictSettles(row, promptId, verdict),
      );
      if (!s || entry?.kind !== "user_message") return;
      const removed =
        verdict === "unavailable" &&
        entry.queued === true &&
        (reason === "removed_by_author" || reason === "removed_by_controller");
      takenBack = removed;
      change = (e) => {
        if (e.kind !== "user_message") return e;
        if (removed) return null; // null: updateSessionCopy's "taken back"
        const settled = withoutQueued(e);
        // Same shape setSendStatus writes: the reason and code never outlive their status.
        const cleared = { statusReason: undefined, statusCode: undefined };
        if (verdict === "unavailable") return { ...settled, status: "failed", ...cleared };
        return settled.status === "unknown"
          ? { ...settled, status: undefined, ...cleared }
          : settled;
      };
      const next = change(entry);
      const at = s.entries.indexOf(entry);
      w.assign(s, [...s.entries.slice(0, at), ...(next ? [next] : []), ...s.entries.slice(at + 1)]);
      this.host.touch(w.agentId);
    });
    if (change) this.updateSessionCopy(promptId, change, takenBack);
  }

  // ─── Pre-turn pending watchdog (PROMPT_PENDING_WATCHDOG_MS) ─────────────
  // Armed by notePromptDispatched when a prompt's relay ack resolves with no
  // turn running yet; cleared by ANY event arriving for the agent but a prompt echo,
  // which proves delivery, not a turn (S060). Reactive map
  // so the route can show the thinking indicator during the pre-turn wait.
  promptPendingAt: Map<string, number> = $state(new Map());

  promptPendingTimers = new Map<string, ReturnType<typeof setTimeout>>();

  promptPendingAnchors = new Map<string, NotRunAnchor>();

  // The row this watchdog appended, paired with the bubble it marked "unknown". One per
  // agent is the whole state: at most one pre-turn wait can be armed for an agent.
  promptPendingErrorRows = new Map<string, { promptEntryId: string; errorEntryId: string }>();

  getPromptPendingSince(agentId: string): number | null {
    return this.promptPendingAt.get(agentId) ?? null;
  }

  /**
   * A prompt for this agent was dispatched and acked (relay forwarded it), but
   * the turn hasn't started. Arm the bounded pre-turn wait: if NOTHING arrives
   * within PROMPT_PENDING_WATCHDOG_MS, mark delivery unknown. Silence cannot
   * establish rejection or justify replaying a command that may have executed.
   * No-op when a turn is already visibly running (nothing pre-turn about it).
   */
  notePromptDispatched(target: ConversationTarget, entryId: string): void {
    return this.history.within(target, "outbox", (w) => {
      const agentId = w.agentId;
      if (this.host.getTurnStatus(agentId) === "running") return;
      // Anchored now: the echo that settles the bubble drops its clientMessageId.
      const sent = this.host.streams.get(agentId)?.entries.find((row) => row.id === entryId);
      const prompt = sent?.kind === "user_message" ? sent : undefined;
      this.promptPendingAnchors.set(agentId, {
        id: prompt?.id,
        clientMessageId: prompt?.clientMessageId,
        messageId: prompt?.messageId,
        content: prompt?.content,
        raisedAt: Date.now(),
        tailId: this.host.streams.get(agentId)?.entries.at(-1)?.id,
      });
      const next = new Map(this.promptPendingAt);
      next.set(agentId, Date.now());
      this.promptPendingAt = next;
      const existing = this.promptPendingTimers.get(agentId);
      if (existing) clearTimeout(existing);
      this.promptPendingTimers.set(
        agentId,
        setTimeout(() => {
          this.promptPendingTimers.delete(agentId);
          if (!this.promptPendingAt.has(agentId)) return;
          const echoed = this.pendingEchoed(agentId);
          const answered = this.pendingAnswered(agentId);
          this.clearPromptPending(agentId);
          const s = this.host.ensure(agentId);
          // Raced: the turn started just as the timer fired, or its answer is already here.
          if (s.turnStatus === "running" || answered) return;
          // Delivered (echoed) yet no turn: not an unconfirmed send, a turn that never started.
          if (echoed) {
            reportUiFailure(
              "agents.turnNeverStarted",
              "the prompt was delivered but its turn never started",
              { agentId, workspaceId: this.host.workspaceForAgent(agentId) },
              { code: "turn_never_started" },
            );
            return;
          }
          // The daemon told us it HOLDS this prompt (agent_prompt_queued): silence is the
          // queue, not a lost delivery, so no "unconfirmed" claim (the bubble keeps its state).
          // Gone from the transcript (moved to the held queue): nothing to mark either.
          const held = s.entries.find((row) => row.id === entryId);
          if (!held) return;
          if (
            held?.kind === "user_message" &&
            held.clientMessageId &&
            mentionQueueState.cyboFor(`c:${held.clientMessageId}`)
          )
            return;
          this.history.lease(agentId, "outbox", (timed) => {
            this.markUnconfirmed(timed, s, entryId);
            return { value: undefined };
          });
        }, PROMPT_PENDING_WATCHDOG_MS),
      );
    });
  }

  // The pre-turn wait ran out: the bubble is "unknown" and an error row says so.
  private markUnconfirmed(w: ConversationWrite, s: AgentStream, entryId: string): void {
    const agentId = w.agentId;
    this.setSendStatus(w, entryId, "unknown");
    const errorEntryId = this.host.nextEntryId();
    w.assign(s, [
      ...s.entries,
      statusCard(
        errorEntryId,
        promptPendingUnconfirmedMessage(Math.round(PROMPT_PENDING_WATCHDOG_MS / 1000)),
        s.entries,
      ),
    ]);
    // Remember the row so a LATER proof that the prompt landed can retract it
    // instead of leaving a contradiction on screen — see retractPromptPendingRow.
    this.promptPendingErrorRows.set(agentId, { promptEntryId: entryId, errorEntryId });
    this.host.capEntries(w, s);
    this.host.touch(w.agentId);
  }

  // The row was a claim about the world and an accepted verdict disproves it, so it
  // comes back off the transcript rather than sitting under the answer contradicting it.
  // `promptId` omitted = the agent itself replied (turn started / assistant text), which
  // proves the unconfirmed prompt landed without needing its verdict.
  retractPromptPendingRow(w: ConversationWrite, promptId?: string): void {
    const agentId = w.agentId;
    const record = this.promptPendingErrorRows.get(agentId);
    if (!record) return;
    const s = this.host.streams.get(agentId);
    // Correlate through the bubble the watchdog marked: the row is retracted only when
    // the verdict names THAT prompt. A verdict for some other send of the same agent
    // says nothing about this one.
    const owns = s?.entries.some(
      (row) =>
        row.id === record.promptEntryId &&
        row.kind === "user_message" &&
        (promptId === undefined || row.clientMessageId === promptId),
    );
    if (!owns) return;
    this.promptPendingErrorRows.delete(agentId);
    if (!s) return;
    w.assign(
      s,
      s.entries.filter((row) => row.id !== record.errorEntryId),
    );
    this.host.touch(w.agentId);
  }

  private pendingEchoed(agentId: string): boolean {
    const promptId = this.promptPendingAnchors.get(agentId)?.clientMessageId;
    return !!promptId && this.host.serverEchoedPromptIds.has(promptId);
  }

  private pendingAnswered(agentId: string): boolean {
    const anchor = this.promptPendingAnchors.get(agentId);
    const s = this.host.streams.get(agentId);
    return !!anchor && !!s && answeredSince(s.entries, anchor);
  }

  // A durable read (catch-up, hydrate) brought the answer in with no live event: the turn ran.
  settleAnsweredPrompt(agentId: string): void {
    if (this.promptPendingAt.has(agentId) && this.pendingAnswered(agentId))
      this.clearPromptPending(agentId);
  }

  // Only a slash command can run out of band; any other prompt's idle frame may precede its turn.
  settleIdleAfterEcho(agentId: string): void {
    const anchor = this.promptPendingAnchors.get(agentId);
    const command =
      !!typedCommandOf(anchor?.clientMessageId) || !!anchor?.content?.trimStart().startsWith("/");
    if (command && this.pendingEchoed(agentId)) this.clearPromptPending(agentId);
  }

  clearPromptPending(agentId: string): void {
    const timer = this.promptPendingTimers.get(agentId);
    if (timer) {
      clearTimeout(timer);
      this.promptPendingTimers.delete(agentId);
    }
    this.promptPendingAnchors.delete(agentId);
    if (!this.promptPendingAt.has(agentId)) return;
    const next = new Map(this.promptPendingAt);
    next.delete(agentId);
    this.promptPendingAt = next;
  }

  // Returns the new entry's id so the caller (app.svelte.ts's dispatchAgentPrompt/
  // retryAgentPrompt) can drive its send-status lifecycle without a separate
  // lookup. `status`/`attachments` are optional — a plain call (no status) is a
  // settled/no-lifecycle add, same as before this field existed.
  // Held or not, decided before anything is painted: a send into a running turn, or a reload of one
  // the daemon held. Only a send with its own id can be settled again.
  private decideHeld(
    agentId: string,
    s: AgentStream,
    status: string | undefined,
    clientMessageId: string | undefined,
    rehydrated: true | undefined,
    heldAtReload: true | undefined,
  ): boolean {
    if (!clientMessageId) return false;
    if (heldAtReload) {
      const confirmed = this.heldByDaemon.get(agentId) ?? new Set<string>();
      this.heldByDaemon.set(agentId, confirmed.add(clientMessageId));
      return true;
    }
    if (status !== "pending" || rehydrated) return false;
    // The host's answer when it has one (it knows a joining provider); else the stream's own turn.
    return this.host.sendWouldQueue
      ? this.host.sendWouldQueue(agentId)
      : s.turnStatus === "running";
  }

  addUserMessage(
    target: ConversationTarget,
    text: string,
    card?: PageCard,
    status?: "pending" | "sent" | "failed" | "unknown",
    attachments?: Attachment[],
    clientMessageId?: string,
    workSessionId?: string,
    rehydrated?: true,
    // A prompt restored from the outbox keeps the time it was SENT, not the reload's.
    sentAt?: number,
    heldAtReload?: true,
  ): string {
    let added: { agentId: string; entry: UserMessageEntry } | undefined; // undefined: nothing drawn yet
    const drawnId = this.history.within(target, "outbox", (w) => {
      const agentId = w.agentId;
      const s = this.host.ensure(agentId);
      const id = this.host.nextEntryId();
      const queued = this.decideHeld(agentId, s, status, clientMessageId, rehydrated, heldAtReload);
      const entry: StreamEntry = {
        id,
        kind: "user_message",
        content: text,
        // Optimistic echo: stamp now (CYBORG-588). A later full hydrate replaces it
        // with the same entry carrying the persisted server ts.
        ts: sentAt ?? Date.now(),
        ...(card ? { card } : {}),
        ...(status ? { status } : {}),
        ...(attachments && attachments.length > 0 ? { attachments } : {}),
        ...(clientMessageId ? { clientMessageId } : {}),
        ...(workSessionId ? { workSessionId } : {}),
        ...(rehydrated ? { rehydrated } : {}),
        ...(queued ? { queued } : {}),
      };
      // A RESTORED prompt is seated at the time it was sent, not at the end (CYBORG-1166):
      // pinning it there drew it below replies that arrived after it. A fresh send keeps
      // the append — its time is `now`, the end IS its place, and seating it by a client
      // clock running behind the daemon's would push it above the reply to the previous
      // prompt and re-parent that reply.
      const at = sentAt === undefined ? -1 : seatByTs(s, entry.ts);
      w.assign(
        s,
        at < 0 ? [...s.entries, entry] : [...s.entries.slice(0, at), entry, ...s.entries.slice(at)],
      );
      this.host.capEntries(w, s);
      this.host.touch(w.agentId);
      if (entry.kind === "user_message") added = { agentId, entry };
      return id;
    });
    if (added && workSessionId)
      this.mirrorIntoSessionBody(added.agentId, workSessionId, added.entry);
    return drawnId;
  }

  // A work session read through its session pages (session-work-mode.ts "fallback") renders
  // `session:<id>`, not the agent's stream: without this copy the person's own prompt stayed
  // off screen until the page re-read at the end of the turn. Keyed by clientMessageId, so
  // the live echo or the next page absorbs it like any optimistic bubble.
  // clientMessageId -> the session body holding the copy and the agent stream holding the original.
  private sessionCopies = new Map<string, { key: string; agentId: string }>();

  private originalCarries(agentId: string, clientMessageId: string): boolean {
    return (this.host.streams.get(agentId)?.entries ?? []).some(
      (e) => e.kind === "user_message" && e.clientMessageId === clientMessageId,
    );
  }

  private mirrorIntoSessionBody(
    agentId: string,
    workSessionId: string,
    entry: UserMessageEntry,
  ): void {
    // Bounded by the live originals: an absorbed or departed one is no longer tracked.
    for (const [cmid, held] of this.sessionCopies) {
      if (!this.originalCarries(held.agentId, cmid)) this.sessionCopies.delete(cmid);
    }
    const key = sessionStreamKey(workSessionId);
    if (key === agentId || !this.host.streams.has(key) || !entry.clientMessageId) return;
    const clientMessageId = entry.clientMessageId;
    this.history.within(key, "outbox", (w) => {
      const s = this.host.ensure(key);
      if (
        s.entries.some((e) => e.kind === "user_message" && e.clientMessageId === clientMessageId)
      ) {
        return;
      }
      const copy: UserMessageEntry = { ...entry, id: this.host.nextEntryId() };
      const at = entry.rehydrated ? seatByTs(s, copy.ts) : -1;
      w.assign(
        s,
        at < 0 ? [...s.entries, copy] : [...s.entries.slice(0, at), copy, ...s.entries.slice(at)],
      );
      this.host.capEntries(w, s);
      this.host.touch(w.agentId);
      this.sessionCopies.set(clientMessageId, { key, agentId });
    });
  }

  // The original a session-body copy stands for, so Retry and Dismiss on the copy act on it
  // (the copy follows; acting on the copy would resend under the session key).
  originalOfSessionCopy(
    key: string,
    entryId: string,
  ): { agentId: string; entryId: string } | undefined {
    const copy = this.host.streams.get(key)?.entries.find((e) => e.id === entryId);
    const cmid = copy?.kind === "user_message" ? copy.clientMessageId : undefined;
    const held = cmid ? this.sessionCopies.get(cmid) : undefined;
    if (!cmid || held?.key !== key) return undefined;
    const original = this.host.streams
      .get(held.agentId)
      ?.entries.find((e) => e.kind === "user_message" && e.clientMessageId === cmid);
    return original ? { agentId: held.agentId, entryId: original.id } : undefined;
  }

  // The session-body copy follows its original: status, reason, or taken back. `settled`: the
  // original is done changing, so the copy is no longer tracked. `last`: it moves to the end.
  private updateSessionCopy(
    clientMessageId: string | undefined,
    change: (e: StreamEntry) => StreamEntry | null,
    settled = false,
    last = false,
  ): void {
    const held = clientMessageId ? this.sessionCopies.get(clientMessageId) : undefined;
    const key = held?.key;
    if (!key || !held || !clientMessageId) return;
    if (settled || !this.originalCarries(held.agentId, clientMessageId)) {
      this.sessionCopies.delete(clientMessageId);
    }
    this.history.within(key, "outbox", (w) => {
      const s = this.host.streams.get(key);
      if (!s) return;
      const idx = s.entries.findIndex(
        (e) => e.kind === "user_message" && e.clientMessageId === clientMessageId,
      );
      if (idx < 0) {
        this.sessionCopies.delete(clientMessageId);
        return;
      }
      const next = change(s.entries[idx]);
      const rest = [...s.entries.slice(0, idx), ...s.entries.slice(idx + 1)];
      if (!next) w.assign(s, rest);
      else if (last) w.assign(s, [...rest, next]);
      else w.assign(s, [...rest.slice(0, idx), next, ...rest.slice(idx)]);
      this.host.touch(w.agentId);
      if (!next) this.sessionCopies.delete(clientMessageId);
    });
  }

  // Take back an optimistic prompt whose send never left the client (a cold work-session
  // spawn that was refused). Matched by the prompt's own client id, and only while it is
  // still unsettled: once an echo has absorbed it the row is the server's, not ours.
  retractOptimisticPrompt(target: ConversationTarget, clientMessageId: string): void {
    this.history.within(target, "outbox", (w) => {
      const agentId = w.agentId;
      const s = this.host.streams.get(agentId);
      if (!s) return;
      const next = s.entries.filter(
        (e) => !(e.kind === "user_message" && e.clientMessageId === clientMessageId),
      );
      if (next.length === s.entries.length) return;
      w.assign(s, next);
      this.host.touch(w.agentId);
    });
    this.updateSessionCopy(clientMessageId, () => null, true);
  }

  // Update a specific optimistic prompt's send status in place (pending / sent /
  // failed), or clear it (undefined) once it's settled — mirrors
  // ChannelLikeState.setSendStatus, keyed by (agentId, entryId) since entries are
  // per-agent rather than a flat list. No-op on a missing agent/entry or a
  // non-user_message entry (defensive; the callers only ever pass a user_message
  // entry's own id).
  // `reason` is the refusal in its own words, shown beside Retry. Optional and
  // always overwritten (including to undefined), so a later status can never
  // inherit the previous failure's explanation.
  setSendStatus(
    target: ConversationTarget,
    entryId: string,
    status: "pending" | "sent" | "failed" | "unknown" | undefined,
    reason?: string,
    code?: string,
  ): void {
    let settledId: string | undefined; // undefined: no entry changed
    // Failed, unconfirmed or waiting on a reason (reconnecting): not held by the daemon, so it leaves
    // the bar and is drawn as a bubble with its Retry. A user's message is never invisible.
    const leavesQueue =
      status === "failed" || status === "unknown" || (status === "pending" && !!reason);
    const write = (e: UserMessageEntry): UserMessageEntry => ({
      ...(leavesQueue ? withoutQueued(e) : e),
      status,
      statusReason: reason,
      statusCode: code,
    });
    this.history.within(target, "outbox", (w) => {
      const agentId = w.agentId;
      const s = this.host.streams.get(agentId);
      if (!s) return;
      const idx = s.entries.findIndex((e) => e.id === entryId);
      if (idx < 0) return;
      const entry = s.entries[idx];
      if (entry.kind !== "user_message") return;
      w.assign(s, [...s.entries.slice(0, idx), write(entry), ...s.entries.slice(idx + 1)]);
      if (leavesQueue && entry.clientMessageId) this.forgetHeld(agentId, entry.clientMessageId);
      this.host.touch(w.agentId);
      settledId = entry.clientMessageId;
    });
    this.updateSessionCopy(settledId, (e) => (e.kind === "user_message" ? write(e) : e));
  }

  // The send's ack names the DM row the relay wrote for this prompt: stamp it so the
  // bubble is reactable now, before the daemon's durable row (which carries it too) lands.
  setPromptSourceMessageId(
    target: ConversationTarget,
    entryId: string,
    sourceMessageId: string,
  ): void {
    return this.history.within(target, "outbox", (w) => {
      const s = this.host.streams.get(w.agentId);
      if (!s) return;
      const idx = s.entries.findIndex((e) => e.id === entryId);
      const entry = idx >= 0 ? s.entries[idx] : undefined;
      if (entry?.kind !== "user_message" || entry.sourceMessageId) return;
      w.assign(s, [
        ...s.entries.slice(0, idx),
        { ...entry, sourceMessageId },
        ...s.entries.slice(idx + 1),
      ]);
      this.host.touch(w.agentId);
    });
  }

  // Transient "sent" checkmark once a prompt's WS round-trip is accepted —
  // mirrors ChannelLikeState.addMessage's reconciliation flash: shows briefly,
  // then auto-clears so the settled steady state carries no indicator (same as a
  // history-hydrated user_message). Only clears if STILL "sent" — a message that
  // raced to "pending"/"failed" again (a fast manual retry) must not be wiped by
  // this stale timer.
  private static readonly SENT_MARKER_MS = 1800;

  private readonly sentMarkers = new Map<string, Set<ReturnType<typeof setTimeout>>>();

  markSent(agentId: string, entryId: string): void {
    this.setSendStatus(agentId, entryId, "sent");
    const markers = this.sentMarkers.get(agentId) ?? new Set();
    const sent = this.host.streams.get(agentId)?.entries.find((e) => e.id === entryId);
    const cmid = sent?.kind === "user_message" ? sent.clientMessageId : undefined;
    const marker = setTimeout(() => {
      markers.delete(marker);
      if (markers.size === 0) this.sentMarkers.delete(agentId);
      const entry = this.host.streams.get(agentId)?.entries.find((e) => e.id === entryId);
      if (entry?.kind === "user_message" && entry.status === "sent") {
        this.setSendStatus(agentId, entryId, undefined);
      } else {
        // The echo absorbed the original: its copy's tick clears too.
        this.updateSessionCopy(cmid, (e) =>
          e.kind === "user_message" && e.status === "sent" ? withoutStatus(e) : e,
        );
      }
    }, PromptOutbox.SENT_MARKER_MS);
    markers.add(marker);
    this.sentMarkers.set(agentId, markers);
  }

  private cancelSentMarkers(agentId: string): void {
    for (const marker of this.sentMarkers.get(agentId) ?? []) clearTimeout(marker);
    this.sentMarkers.delete(agentId);
  }

  forget(agentId: string): void {
    this.cancelSentMarkers(agentId);
    this.clearPromptPending(agentId);
    this.promptPendingErrorRows.delete(agentId);
    this.ranFromQueue.delete(agentId);
    this.heldByDaemon.delete(agentId);
  }

  // Nothing armed survives a logout.
  reset(): void {
    for (const agentId of this.sentMarkers.keys()) this.cancelSentMarkers(agentId);
    for (const id of [...this.promptPendingTimers.keys(), ...this.promptPendingAt.keys()]) {
      this.clearPromptPending(id);
    }
    this.promptPendingErrorRows.clear();
    this.ranFromQueue.clear();
    this.heldByDaemon.clear();
  }

  // Retry a failed or unconfirmed prompt through app.svelte.ts's `dispatch`, at once: a busy
  // daemon holds it. Same clientMessageId, so the relay and the owner's ledger dedupe it.
  retryPrompt(
    agentId: string,
    entryId: string,
    dispatch: (
      text: string,
      attachments?: Attachment[],
      pageRef?: string,
      clientMessageId?: string,
      workSessionId?: string,
      command?: AgentCommandInput,
    ) => void,
  ): void {
    const send = this.takeRetrySend(agentId, entryId);
    if (!send) return;
    const args = [
      send.text,
      send.attachments,
      send.pageRef,
      send.clientMessageId,
      send.workSessionId,
    ] as const;
    if (send.command) dispatch(...args, send.command);
    else dispatch(...args);
  }

  private takeRetrySend(agentId: string, entryId: string): RetrySend | undefined {
    const entry = this.host.getEntries(agentId).find((e) => e.id === entryId);
    if (entry?.kind !== "user_message") return undefined;
    if (entry.status !== "failed" && entry.status !== "unknown") return undefined;
    const pageRef = entry.card?.kind === "page" ? entry.card.pageId : undefined;
    // A failed COMMAND bubble is retried as the command, never as the prose that reads the same.
    const command = typedCommandOf(entry.clientMessageId);
    return {
      text: entry.content,
      attachments: entry.attachments,
      pageRef,
      clientMessageId: entry.clientMessageId,
      workSessionId: entry.workSessionId,
      ...(command ? { command } : {}),
    };
  }

  /**
   * The human's exit from an unconfirmed delivery. Nothing else can settle it: after a
   * reload no verdict can arrive for that prompt, so the row would sit unconfirmed for
   * good and its outbox record with it. Dismissing resends nothing and deletes nothing
   * — it drops the row's tracking, which is the only honest thing to do with a state
   * only the person can decide about. Returns the id the caller drops from the outbox,
   * or null when this entry is not an unconfirmed prompt.
   */
  dismissUnknownPrompt(target: ConversationTarget, entryId: string): string | null {
    const dismissed = this.history.within(target, "outbox", (w) => {
      const agentId = w.agentId;
      const stream = this.host.streams.get(agentId);
      const entry = stream?.entries.find((row) => row.id === entryId);
      if (!stream || !entry || entry.kind !== "user_message" || entry.status !== "unknown") {
        return null;
      }
      const clientMessageId = entry.clientMessageId ?? null;
      const { status: _status, ...settled } = entry;
      w.assign(
        stream,
        stream.entries.map((row) => (row.id === entryId ? settled : row)),
      );
      this.host.touch(w.agentId);
      return clientMessageId;
    });
    this.updateSessionCopy(
      dismissed ?? undefined,
      (e) => (e.kind === "user_message" ? withoutStatus(e) : e),
      true,
    );
    return dismissed;
  }
}
